import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { FileText, Clock, User, AlertCircle, Filter } from 'lucide-react';
import { Input } from '@/components/ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlatformAdminAudit = () => {
  const { getAuthHeaders } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const response = await axios.get(`${API}/platform-admin/audit-log`, { headers: getAuthHeaders() });
        setLogs(response.data);
      } catch (error) {
        console.error('Failed to fetch audit log:', error);
      }
      setLoading(false);
    };
    fetchLogs();
  }, [getAuthHeaders]);

  const getActionColor = (action) => {
    switch (action) {
      case 'suspend_user': return 'text-red-400 bg-red-500/20';
      case 'activate_user': return 'text-green-400 bg-green-500/20';
      case 'update_settings': return 'text-blue-400 bg-blue-500/20';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getActionLabel = (action) => {
    switch (action) {
      case 'suspend_user': return 'User Suspended';
      case 'activate_user': return 'User Activated';
      case 'update_settings': return 'Settings Updated';
      default: return action;
    }
  };

  const filteredLogs = logs.filter(log => {
    if (!filter) return true;
    const query = filter.toLowerCase();
    return (
      log.action?.toLowerCase().includes(query) ||
      log.user_type?.toLowerCase().includes(query) ||
      log.admin_id?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl uppercase tracking-tight">Audit Log</h1>
        <p className="text-muted-foreground">Track all administrative actions on the platform</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border/40 p-4 rounded-sm">
          <FileText className="text-primary mb-2" size={20} />
          <p className="text-2xl font-heading">{logs.length}</p>
          <p className="text-xs text-muted-foreground uppercase">Total Entries</p>
        </div>
        <div className="bg-card border border-border/40 p-4 rounded-sm">
          <User className="text-red-400 mb-2" size={20} />
          <p className="text-2xl font-heading">
            {logs.filter(l => l.action === 'suspend_user').length}
          </p>
          <p className="text-xs text-muted-foreground uppercase">Suspensions</p>
        </div>
        <div className="bg-card border border-border/40 p-4 rounded-sm">
          <User className="text-green-400 mb-2" size={20} />
          <p className="text-2xl font-heading">
            {logs.filter(l => l.action === 'activate_user').length}
          </p>
          <p className="text-xs text-muted-foreground uppercase">Activations</p>
        </div>
        <div className="bg-card border border-border/40 p-4 rounded-sm">
          <Clock className="text-blue-400 mb-2" size={20} />
          <p className="text-2xl font-heading">
            {logs.filter(l => l.action === 'update_settings').length}
          </p>
          <p className="text-xs text-muted-foreground uppercase">Settings Changes</p>
        </div>
      </div>

      {/* Filter */}
      <div className="relative">
        <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
        <Input
          placeholder="Filter by action, user type..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Log List */}
      <div className="bg-card border border-border/40 rounded-sm">
        {filteredLogs.length === 0 ? (
          <div className="p-8 text-center">
            <AlertCircle className="mx-auto text-muted-foreground mb-4" size={32} />
            <p className="text-muted-foreground">No audit log entries found</p>
          </div>
        ) : (
          <div className="divide-y divide-border/40">
            {filteredLogs.map((log, idx) => (
              <div key={idx} className="p-4 hover:bg-muted/20 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`px-2 py-1 rounded text-xs uppercase ${getActionColor(log.action)}`}>
                      {getActionLabel(log.action)}
                    </div>
                    <div>
                      {log.user_type && log.user_id && (
                        <p className="text-sm">
                          <span className="text-muted-foreground">Target:</span>{' '}
                          <span className="font-medium">{log.user_type}</span>{' '}
                          <span className="text-muted-foreground text-xs">({log.user_id.slice(0, 8)}...)</span>
                        </p>
                      )}
                      {log.changes && (
                        <details className="mt-2">
                          <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                            View changes
                          </summary>
                          <pre className="mt-2 p-2 bg-muted/30 rounded text-xs overflow-auto max-h-32">
                            {JSON.stringify(log.changes, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>
                  <div className="text-right text-sm">
                    <p className="text-muted-foreground">
                      {log.timestamp 
                        ? new Date(log.timestamp).toLocaleDateString()
                        : 'Unknown'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {log.timestamp 
                        ? new Date(log.timestamp).toLocaleTimeString()
                        : ''}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlatformAdminAudit;
